import { unwrap, uploadAsset } from "@sagot/api-client";
import { contentTypeFor } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import type { InstallStatusOut, SnippetsOut, WidgetDraftIn, WidgetOut } from "../shared/types";
import type { BlockedIp } from "./security-model";

const path = (agentId: string) => ({ params: { path: { agent_id: agentId } } });

export function useWidget(agentId: string): UseQueryResult<WidgetOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.widget(wsId, agentId), queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/widget", path(agentId))) });
}

export function useSnippets(agentId: string, enabled: boolean): UseQueryResult<SnippetsOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.snippets(wsId, agentId),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/widget/snippets", path(agentId))),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Install detection; `refetch()` is the "Re-check" action. Seeded from the widget payload. */
export function useInstallStatus(agentId: string, initial: InstallStatusOut | undefined): UseQueryResult<InstallStatusOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.installStatus(wsId, agentId),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/widget/install-status", path(agentId))),
    initialData: initial,
    staleTime: 30_000,
  });
}

function useWidgetCache(agentId: string) {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return (widget: WidgetOut) => {
    queryClient.setQueryData(dkKeys.widget(wsId, agentId), widget);
  };
}

export function useSaveDraft(agentId: string) {
  const api = useApi();
  const store = useWidgetCache(agentId);
  return useMutation({
    mutationFn: (body: WidgetDraftIn) => unwrap(api.PUT("/v1/agents/{agent_id}/widget", { ...path(agentId), body })),
    onSuccess: store,
  });
}

export function usePublish(agentId: string) {
  const api = useApi();
  const store = useWidgetCache(agentId);
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agents/{agent_id}/widget/publish", path(agentId))),
    onSuccess: store,
  });
}

export interface SecurityInput {
  allowed_domains: string[];
  identity_verification: boolean;
  rate_limits: { messages_per_visitor_hour: number; new_chats_per_ip_day: number };
  blocked_ips: BlockedIp[];
}

export function useSaveSecurity(agentId: string) {
  const api = useApi();
  const store = useWidgetCache(agentId);
  return useMutation({
    mutationFn: (body: SecurityInput) => unwrap(api.PUT("/v1/agents/{agent_id}/widget/security", { ...path(agentId), body })),
    onSuccess: store,
  });
}

/** The new secret lives only in the mutation result (shown once; never cached or refetched). */
export function useRotateSecret(agentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agents/{agent_id}/widget/identity-secret/rotate", path(agentId))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dkKeys.widget(wsId, agentId), exact: true }),
  });
}

export function useEmailInstructions(agentId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { email: string; note: string }) => unwrap(api.POST("/v1/agents/{agent_id}/widget/install-email", { ...path(agentId), body })),
  });
}

export interface UploadedImage {
  url: string;
}

/**
 * Brand image upload (chat avatar and launcher icon take the same path): presign
 * (`POST …/widget/assets`) → PUT the bytes → public URL. Failures are `UploadError`s tagged with
 * their stage; the PUT carries the canonical Content-Type the API signed (`onProgress`: whole percent). The API checks the
 * finished upload (type, size, dimensions, virus scan) when the draft is saved with the URL.
 */
export function useBrandImageUpload(agentId: string, onProgress?: (percent: number) => void) {
  const api = useApi();
  return useMutation({
    mutationFn: async (file: File): Promise<UploadedImage> => {
      const request = () => unwrap(api.POST("/v1/agents/{agent_id}/widget/assets", { ...path(agentId), body: { filename: file.name, content_type: contentTypeFor(file), size_bytes: file.size } }));
      return { url: await uploadAsset(request, file, { onProgress }) };
    },
  });
}
